import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { GetGuestsResponse as SearchGuestsResponse } from '../models/GetGuestsResponse';
import { createDownloadAttachmentLink } from "../../../utilities/FileUtility";
import { Logger } from '../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { PortalBeHttpClient } from '../../../infrastructure/http/legacy/PortalBeHttpClient';
import { SearchParams } from '../../../shared/search/SearchParams';
import { GuestScreeningRequest } from '../guest-screening/models/GuestScreeningRequest.model';
import { GuestDetails } from '../models/GuestDetails';
import { AdminPortalAPIHttpClient } from '../../../infrastructure/http/v2/AdminPortalAPIHttpClient';

@Injectable({
  providedIn: 'root'
})
export class GuestService {

  lastSearchRequest?: SearchParams;

  constructor(
    private adminPortalApiHttpClient: AdminPortalAPIHttpClient,
    private portalBeHttpClient: PortalBeHttpClient,
    private logService: Logger,
    private userContextProvider: UserContextProvider
  ) { }

  searchGuests = async (searchRequest: SearchParams): Promise<SearchGuestsResponse> => {
    try {
      this.lastSearchRequest = searchRequest;

      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post('/guests/search', {...searchRequest}));
      if (!response) {
        return Promise.reject(`Search guests response null`);
      }

      return Promise.resolve({
        guests: response.data,
        totalRecords: response.metaData.totalRecords
      });
    } catch(e) {
      return Promise.reject(`Error while searching guests ${JSON.stringify(e)}`);
    }
  }

  async getGuests(): Promise<any[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/guests`));
      if (!response) {
        return Promise.reject('Get guests response null');
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving guests ${e}`);
    }
  }

  getGuestDetails = async (guestId: number): Promise<GuestDetails> => {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/guests/${guestId}`));
      if (!response) {
        return Promise.reject(`Get guest details response null ${guestId}`);
      }

      return Promise.resolve(response.data);
    } catch(e) {
      return Promise.reject(`Error while retrieving guest details ${guestId} ${e}`);
    }
  }

  getGuestVerificationPdf = async (guestId?: number, reservationId?: number) => {
    if (!guestId) {
      throw new Error('Invalid guest, cannot download PDF');
    }
    
    try {
      const headers = { 'Authorization': `Bearer ${this.userContextProvider.token}`, 'Content-type': 'application/json' }
      let url = `/guests/export-pdf?id=${guestId}`;
      if(reservationId) {
        url += `&reservationId=${reservationId}`;
      }

      const response: any = await firstValueFrom(this.portalBeHttpClient.get(url, null, null, headers));
      if (!response || !response.data || !response.data.fileName || !response.data.base64Url) {
        throw new Error(`Download guest verification PDF response null ${guestId}`);
      }

      const {
        fileName,
        base64Url
      } = response.data;

      createDownloadAttachmentLink(fileName, base64Url);
    } catch (e) {
      throw new Error(`Error while downloading guest verification PDF ${guestId} ${JSON.stringify(e)}`);
    }
  }

  reverifyGuest = async (request: any) => {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/guests/re-verify`, request));
      if (!response) {
        return Promise.reject('Re-verify guest response null');
      }

      return Promise.resolve(response.data);
    } catch(e) {
      return Promise.reject(`Error while re-verifying guest: ${JSON.stringify(request)} ${e}`);
    }
  }

  createGuestVerification = async (request: GuestScreeningRequest) => {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/guests/verify`, request));
      if (!response) {
        return Promise.reject('Create guest verification response null');
      }

      return Promise.resolve(response.data);
    } catch(e) {
      return Promise.reject(`Error while creating guest verification ${JSON.stringify(request)} ${e}`);
    }
  }

  async sendReminder(guestId: number, landingPageUrl: string, reservationId: number, saveEmailAddress: boolean, subject: string, emailAddress: string) {
    try {
      await firstValueFrom(this.adminPortalApiHttpClient.post(`/guests/${guestId}/send-reminder`, {
        guest_id: guestId,
        landing_page_url: landingPageUrl,
        reservation_id: reservationId,
        save_email_address: saveEmailAddress,
        subject: subject,
        to_email_address: emailAddress
      }));
    } catch (e) {
      this.logService.handleError(e, `Error while sending guest reminder email ${emailAddress}`, `Error while sending guest reminder email ${emailAddress}`);
      return Promise.reject(`Error while sending guest reminder email ${emailAddress} ${e}`);
    }
  }

  async sendQuestion(guestId: any, question: string) {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/guests/${guestId}/send-question`, {
        guest_id: guestId,
        question: question
      }));
      if(!response) {
        throw new Error(`Send question for guest response null [${guestId}]`);
      }
      return Promise.resolve(response);
    } catch (e) {
      this.logService.handleError(e, `Error while sending question for guest ${guestId}`, `Error while sending question for guest ${guestId}`);
      return Promise.reject(`Error while sending question for guest [${guestId}] ${e}`);
    }
  }

  async sendDecline(guestId: any, reason: string) {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/guests/${guestId}/decline`, {
        guest_id: guestId,
        reason: reason
      }));
      if (!response) {
        throw new Error(`Send decline for guest response null [${guestId}]`);
      }
      return Promise.resolve(response);
    } catch (e) {
      this.logService.handleError(e, `Error while sending decline for guest ${guestId}`, `Error while sending decline for guest ${guestId}`);
      return Promise.reject(`Error while sending decline for guest [${guestId}] ${e}`);
    }
  }

  async updateGuestVerificationResultDetail(payload: any) {
    try {
      const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post(`/guests/update-verification-result-detail`, payload));
      if (!response) {
        return Promise.reject('Update guest verification result response null');
      }

      return Promise.resolve(response);
    } catch(e) {
      return Promise.reject(`Error while update guest verification result detail ${JSON.stringify(payload)}: ${e}`);
    }
  }
}